import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import uploadFiles from '~/utils/uploadFiles';

// Mirrors the backend's BadRequestError message for security scan rejection.
const SECURITY_SCAN_REJECTED_MESSAGE = 'File rejected by security scan';

type ChatUploadErrorBody = {
    message?: string;
};

export type ChatMediaUpload = {
    key: string;
    fileExtension: string;
    mimeType: string;
    name: string;
};

// mediaType omitted — derived server-side from mimeType.
export const useUploadMediaMutation = (chatId: string) => {
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file);

            const response = await uploadFiles(`/chat/upload?chatId=${chatId}`, {
                body: formData,
                method: 'post',
            });

            if (!response.ok) {
                const body = response.body as ChatUploadErrorBody | null;
                if (body?.message === SECURITY_SCAN_REJECTED_MESSAGE) {
                    throw new Error(t('FILE_UPLOAD.MALWARE_REJECTED', { files: file.name }));
                }
                throw new Error(t('WHATSAPP_CHAT.ERROR.UPLOAD_FAILED'));
            }
            // uploadFiles returns parsed JSON in body, not a real Response stream.
            return response.body as unknown as ChatMediaUpload;
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });
};
